// Phase 10 — /404 (SPA catch-all).
//
// Mounted inside PublicLayout so the page gains the marketing nav +
// footer. Emits <meta name="prerender-status-code" content="404"> via
// SeoHead so headless renderers can surface the correct response code
// (the SPA shell itself serves HTTP 200 for any unknown path by design).
//
// Recovery grid points visitors at the four most useful public pages
// so a broken / outdated link doesn't dead-end the session.

import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    BookOpen,
    Layers,
    Play,
    ShieldCheck,
} from "lucide-react";
import SeoHead from "@/components/SeoHead";
import { buildBreadcrumbListSchema } from "@/lib/schema";

const RECOVERY_CARDS = [
    {
        to: "/features",
        label: "Counter-UAS simulation features",
        description: "Five outcomes the operator console delivers.",
        icon: Layers,
        testId: "notfound-card-features",
    },
    {
        to: "/demo",
        label: "Verified intercept demonstrations",
        description: "Six real runs. Trajectories, miss distance, outcome.",
        icon: Play,
        testId: "notfound-card-demo",
    },
    {
        to: "/security",
        label: "Security posture",
        description: "Multi-tenant isolation, audit trail, compliance.",
        icon: ShieldCheck,
        testId: "notfound-card-security",
    },
    {
        to: "/contact",
        label: "Request access",
        description: "Single-use invite links. Five-day onboarding.",
        icon: BookOpen,
        testId: "notfound-card-contact",
    },
];

export default function NotFoundPage() {
    return (
        <>
            <SeoHead
                path="/"
                title="Page not found — UAVSimNet"
                description="The page you were looking for isn't here. Jump to UAVSimNet's features, security posture, demos, or access request form."
                noIndex
                prerenderStatusCode={404}
                schemaJsonLd={[
                    buildBreadcrumbListSchema({
                        path: "/",
                        label: "Page not found",
                    }),
                ]}
            />
            <section
                className="section"
                style={{ paddingTop: 96, paddingBottom: 96 }}
                data-testid="notfound-page"
            >
                <div className="section__inner">
                    <span
                        className="section__eyebrow"
                        data-testid="notfound-eyebrow"
                    >
                        // 404
                    </span>
                    <h1
                        className="section__heading"
                        style={{ marginTop: 12 }}
                        data-testid="notfound-h1"
                    >
                        Off the radar
                    </h1>
                    <p
                        className="section__lede"
                        style={{ maxWidth: 720 }}
                        data-testid="notfound-lede"
                    >
                        The page you were looking for isn't here — it may have moved with the
                        Phase&nbsp;9 information-architecture refresh, or you may have
                        followed a stale link. A few useful places to land instead:
                    </p>

                    <div
                        style={{
                            marginTop: 32,
                            display: "grid",
                            gridTemplateColumns:
                                "repeat(auto-fit, minmax(260px, 1fr))",
                            gap: 16,
                        }}
                        data-testid="notfound-recovery-grid"
                    >
                        {RECOVERY_CARDS.map((c) => (
                            <Link
                                key={c.to}
                                to={c.to}
                                className="cap-card"
                                style={{ textDecoration: "none" }}
                                data-testid={c.testId}
                            >
                                <div className="cap-card__icon">
                                    <c.icon size={20} />
                                </div>
                                <div
                                    style={{
                                        fontFamily: "JetBrains Mono, monospace",
                                        color: "var(--ux-text-1)",
                                        marginBottom: 6,
                                    }}
                                >
                                    {c.label}
                                </div>
                                <div
                                    style={{
                                        color: "var(--ux-text-3)",
                                        fontSize: 13,
                                    }}
                                >
                                    {c.description}
                                </div>
                                <div
                                    style={{
                                        marginTop: 12,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 4,
                                        color: "var(--ux-accent)",
                                        fontSize: 12,
                                        fontFamily: "JetBrains Mono, monospace",
                                    }}
                                >
                                    go →
                                </div>
                            </Link>
                        ))}
                    </div>

                    <div style={{ marginTop: 40 }}>
                        <Link
                            to="/"
                            className="btn-primary"
                            data-testid="notfound-home-link"
                        >
                            Return home <ArrowUpRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}
